import { useState, useEffect, useCallback, useRef } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import {
    Bot, Loader2, Radar, CheckCircle2, XCircle, TrendingUp, TrendingDown,
    BookOpen, Clock, AlertTriangle, Sparkles, Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVALS = [1, 2, 4, 6, 8, 12, 24];

function DirectionBadge({ direction }) {
    const isBuy = direction === 'BUY';
    const color = isBuy ? '#10B981' : '#EF4444';
    const Icon = isBuy ? TrendingUp : TrendingDown;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold tracking-wide"
            style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}40` }}
            data-testid={`draft-direction-${direction?.toLowerCase()}`}
        >
            <Icon className="w-3 h-3" />
            {direction}
        </span>
    );
}

function ConfidenceBar({ value }) {
    const color = value >= 80 ? '#059669' : value >= 65 ? 'var(--primary-color)' : '#D97706';
    return (
        <div className="flex items-center gap-2" data-testid="draft-confidence">
            <div className="w-24 h-1.5 rounded-full bg-black/10 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: color }} />
            </div>
            <span className="text-xs font-semibold" style={{ color }}>{value}%</span>
        </div>
    );
}

function DraftCard({ draft, onApprove, onReject, deciding }) {
    return (
        <div
            className="rounded-lg p-5 space-y-4"
            style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' }}
            data-testid={`engine-draft-card-${draft.market}`}
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <span className="text-lg font-bold tracking-wide" data-testid="draft-market">{draft.market}</span>
                    <DirectionBadge direction={draft.direction} />
                    {draft.setup_type ? (
                        <span className="text-xs px-2 py-0.5 rounded bg-black/5 text-[color:var(--text-secondary)] border border-black/10">
                            {draft.setup_type}
                        </span>
                    ) : draft.timeframe && (
                        <span className="text-xs px-2 py-0.5 rounded bg-black/5 text-[color:var(--text-secondary)] border border-black/10">
                            {draft.timeframe} range
                        </span>
                    )}
                </div>
                <ConfidenceBar value={draft.confidence || 0} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div>
                    <div className="text-xs text-[color:var(--text-muted)] mb-0.5">Entry</div>
                    <div className="font-semibold" data-testid="draft-entry">{draft.entry_price}</div>
                </div>
                <div>
                    <div className="text-xs text-[color:var(--text-muted)] mb-0.5">Stop Loss</div>
                    <div className="font-semibold text-red-600" data-testid="draft-sl">{draft.stop_loss}</div>
                </div>
                <div>
                    <div className="text-xs text-[color:var(--text-muted)] mb-0.5">Take Profit</div>
                    <div className="font-semibold text-emerald-600" data-testid="draft-tp">{draft.take_profit}</div>
                </div>
                <div>
                    <div className="text-xs text-[color:var(--text-muted)] mb-0.5">Planned R:R</div>
                    <div className="font-semibold" data-testid="draft-rr">{draft.rr_planned ? `1:${draft.rr_planned}` : '—'}</div>
                </div>
            </div>

            {draft.reasoning && (
                <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed border-l-2 pl-3" style={{ borderColor: 'rgba(184,134,11,0.5)' }} data-testid="draft-reasoning">
                    {draft.reasoning}
                </p>
            )}

            {draft.image_url && (
                <img
                    src={`${API}/files/${draft.image_url}`}
                    alt={`${draft.market} analysis chart`}
                    className="w-full rounded-lg border"
                    style={{ borderColor: 'rgba(255,255,255,0.08)' }}
                    data-testid="draft-chart-image"
                />
            )}

            <div className="flex items-center justify-between gap-3 pt-1">
                <span className="text-xs text-[color:var(--text-muted)]">
                    {draft.created_at ? new Date(draft.created_at).toLocaleString() : ''}
                    {' · '}rulebook: {draft.rulebook_source === 'baseline' ? 'Baseline CRT' : 'Video-trained'}
                </span>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onReject(draft)}
                        disabled={deciding}
                        data-testid="draft-reject-btn"
                    >
                        <XCircle className="w-4 h-4 mr-1.5" />
                        Reject
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => onApprove(draft)}
                        disabled={deciding}
                        data-testid="draft-approve-btn"
                    >
                        {deciding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                        Approve & Post
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default function AdminEngine() {
    const [status, setStatus] = useState(null);
    const [drafts, setDrafts] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanRequested, setScanRequested] = useState(false);
    const [decidingId, setDecidingId] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);
    const [minConf, setMinConf] = useState('');
    const [tgStatus, setTgStatus] = useState(null);
    const [freeChatId, setFreeChatId] = useState('');
    const [freeBusy, setFreeBusy] = useState(false);
    const pollRef = useRef(null);

    const fetchAll = useCallback(async () => {
        try {
            const [stRes, dRes, hRes, tgRes] = await Promise.all([
                axios.get(`${API}/admin/engine/status`, { withCredentials: true }),
                axios.get(`${API}/admin/engine/drafts`, { params: { status: 'pending' }, withCredentials: true }),
                axios.get(`${API}/admin/engine/drafts`, { params: { limit: 10 }, withCredentials: true }),
                axios.get(`${API}/admin/telegram/status`, { withCredentials: true }),
            ]);
            setStatus(stRes.data);
            setDrafts(dRes.data);
            setHistory(hRes.data.filter((d) => d.status !== 'pending').slice(0, 8));
            setMinConf((v) => (v === '' ? String(stRes.data.settings.min_confidence ?? 60) : v));
            setTgStatus(tgRes.data);
            setFreeChatId((v) => (v === '' ? (tgRes.data.free_chat_id || '') : v));
            return stRes.data;
        } catch (err) {
            toast.error('Failed to load engine status');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
        return () => clearInterval(pollRef.current);
    }, [fetchAll]);

    // Poll while a scan is running
    useEffect(() => {
        const scanning = status?.scan_in_progress || scanRequested;
        clearInterval(pollRef.current);
        if (scanning) {
            pollRef.current = setInterval(async () => {
                const st = await fetchAll();
                if (st && !st.scan_in_progress) {
                    setScanRequested(false);
                    const summary = st.last_scan_summary;
                    if (summary) {
                        toast.success(`Scan complete — ${summary.drafts_created} setup(s) found across ${summary.results?.length || 0} markets`);
                    }
                }
            }, 5000);
        }
        return () => clearInterval(pollRef.current);
    }, [status?.scan_in_progress, scanRequested, fetchAll]);

    const startScan = async () => {
        try {
            const res = await axios.post(`${API}/admin/engine/scan`, {}, { withCredentials: true });
            if (res.data.started) {
                setScanRequested(true);
                toast.info('Scan started — analysing all 6 markets with CRT. This takes 1-3 minutes.');
            } else {
                toast.warning(res.data.message || 'Scan already running');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to start scan');
        }
    };

    const saveSettings = async (patch) => {
        setSavingSettings(true);
        try {
            const res = await axios.put(`${API}/admin/engine/settings`, patch, { withCredentials: true });
            setStatus((s) => ({ ...s, settings: res.data }));
            toast.success('Engine settings saved');
        } catch (err) {
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const approve = async (draft) => {
        setDecidingId(draft.id);
        try {
            await axios.post(`${API}/admin/engine/drafts/${draft.id}/approve`, {}, { withCredentials: true });
            toast.success(`${draft.market} signal published to students & Telegram`);
            await fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to approve draft');
        } finally {
            setDecidingId(null);
        }
    };

    const reject = async (draft) => {
        setDecidingId(draft.id);
        try {
            await axios.post(`${API}/admin/engine/drafts/${draft.id}/reject`, {}, { withCredentials: true });
            toast.success('Draft rejected');
            await fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to reject draft');
        } finally {
            setDecidingId(null);
        }
    };

    const saveFreeChat = async () => {
        setFreeBusy(true);
        try {
            const res = await axios.put(`${API}/admin/telegram/free-config`, { chat_id: freeChatId.trim() }, { withCredentials: true });
            setTgStatus(res.data);
            toast.success('Free channel saved');
        } catch (err) {
            toast.error('Failed to save free channel');
        } finally {
            setFreeBusy(false);
        }
    };

    const detectFreeChat = async () => {
        setFreeBusy(true);
        try {
            const res = await axios.post(`${API}/admin/telegram/free-detect`, {}, { withCredentials: true });
            if (res.data.ok && res.data.chats?.length) {
                const c = res.data.chats[0];
                setFreeChatId(c.chat_id);
                toast.success(`Detected: ${c.title} (${c.chat_id}) — click Save to use it`);
            } else {
                toast.warning(res.data.error || 'No channels detected. Add @NikayofxBot as admin to your free channel, post any message there, then retry.');
            }
        } catch (err) {
            toast.error('Detection failed');
        } finally {
            setFreeBusy(false);
        }
    };

    const testFreeChat = async () => {
        setFreeBusy(true);
        try {
            const res = await axios.post(`${API}/admin/telegram/free-test`, {}, { withCredentials: true });
            if (res.data.ok) toast.success('Test message sent to the free channel!');
            else toast.error(res.data.error || 'Test failed');
        } catch (err) {
            toast.error('Test failed');
        } finally {
            setFreeBusy(false);
        }
    };

    const scanning = status?.scan_in_progress || scanRequested;
    const settings = status?.settings || {};

    // Arabic channels state
    const [arVip, setArVip] = useState('');
    const [arFree, setArFree] = useState('');
    const [arBusy, setArBusy] = useState(false);
    const [arStatus, setArStatus] = useState(null);

    useEffect(() => {
        axios.get(`${API}/admin/telegram/arabic-config`, { withCredentials: true })
            .then((r) => {
                setArStatus(r.data);
                setArVip(r.data.vip_chat_id || '');
                setArFree(r.data.free_chat_id || '');
            })
            .catch(() => {});
    }, []);

    const detectArabic = async () => {
        setArBusy(true);
        try {
            const res = await axios.post(`${API}/admin/telegram/arabic-detect`, {}, { withCredentials: true });
            if (res.data.ok && (res.data.vip || res.data.free)) {
                if (res.data.vip) setArVip(res.data.vip.chat_id);
                if (res.data.free) setArFree(res.data.free.chat_id);
                toast.success(`Detected — VIP: ${res.data.vip?.title || 'not found'} · Free: ${res.data.free?.title || 'not found'}. Click Save.`);
            } else {
                toast.warning(res.data.error || 'Not found. Send "vip arabic" in the VIP group and "free arabic" in the free group (bot must be admin), then retry.');
            }
        } catch (e) {
            toast.error('Detection failed');
        } finally {
            setArBusy(false);
        }
    };

    const saveArabic = async () => {
        setArBusy(true);
        try {
            const res = await axios.put(`${API}/admin/telegram/arabic-config`, { vip_chat_id: arVip.trim(), free_chat_id: arFree.trim() }, { withCredentials: true });
            setArStatus(res.data);
            toast.success('Arabic channels saved');
        } catch (e) {
            toast.error('Failed to save');
        } finally {
            setArBusy(false);
        }
    };

    const testArabic = async () => {
        setArBusy(true);
        try {
            const res = await axios.post(`${API}/admin/telegram/arabic-test`, {}, { withCredentials: true });
            const ok = (k) => res.data[k]?.ok ? '✓' : `✗ ${res.data[k]?.error || ''}`;
            toast.info(`VIP: ${ok('vip')} · Free: ${ok('free')}`);
        } catch (e) {
            toast.error('Test failed');
        } finally {
            setArBusy(false);
        }
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8" data-testid="admin-engine-page">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                            <Bot className="w-7 h-7" style={{ color: 'var(--primary-color)' }} />
                            CRT Signal Engine
                        </h1>
                        <p className="text-sm text-[color:var(--text-secondary)] mt-1.5">
                            AI-powered market analysis using Candle Range Theory across {status?.markets?.join(', ') || '6 markets'}
                        </p>
                    </div>
                    <Button onClick={startScan} disabled={scanning || loading} data-testid="engine-scan-now-btn">
                        {scanning ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Scanning…
                            </>
                        ) : (
                            <>
                                <Radar className="w-4 h-4 mr-2" />
                                Scan Now
                            </>
                        )}
                    </Button>
                </div>

                {/* Rulebook status banner */}
                <div
                    className="rounded-lg px-4 py-3 flex items-start gap-3 text-sm"
                    style={{ backgroundColor: 'rgba(184,134,11,0.08)', border: '1px solid rgba(184,134,11,0.3)' }}
                    data-testid="engine-rulebook-banner"
                >
                    <BookOpen className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#22D3EE' }} />
                    {status?.rulebook_source === 'baseline' ? (
                        <span className="text-[color:var(--text-primary)]">
                            Engine is running on the <b>Baseline CRT rulebook</b>. Once you upload your CRT lesson videos,
                            they will be processed into a <b>video-trained rulebook</b> that replaces this baseline for higher-accuracy analysis.
                        </span>
                    ) : (
                        <span className="text-[color:var(--text-primary)] flex items-center gap-2">
                            <Sparkles className="w-4 h-4" style={{ color: '#10B981' }} />
                            Engine is running on your <b>video-trained CRT rulebook</b>.
                        </span>
                    )}
                </div>

                {/* Settings */}
                <div
                    className="rounded-lg p-5"
                    style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' }}
                    data-testid="engine-settings-card"
                >
                    <h2 className="text-base font-semibold mb-4">Auto-Scan Settings</h2>
                    <div className="grid sm:grid-cols-4 gap-6">
                        <div className="flex items-center justify-between sm:justify-start sm:gap-4">
                            <Label className="text-sm text-[color:var(--text-secondary)]">Auto-scan</Label>
                            <Switch
                                checked={!!settings.auto_scan_enabled}
                                onCheckedChange={(v) => saveSettings({ auto_scan_enabled: v })}
                                disabled={savingSettings}
                                data-testid="engine-autoscan-toggle"
                            />
                        </div>
                        <div className="flex items-center justify-between sm:justify-start sm:gap-4">
                            <Label className="text-sm text-[color:var(--text-secondary)]">Auto TP/SL tracking</Label>
                            <Switch
                                checked={settings.auto_outcome_enabled !== false}
                                onCheckedChange={(v) => saveSettings({ auto_outcome_enabled: v })}
                                disabled={savingSettings}
                                data-testid="engine-auto-outcome-toggle"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm text-[color:var(--text-secondary)]">Scan every</Label>
                            <Select
                                value={String(settings.scan_interval_hours || 4)}
                                onValueChange={(v) => saveSettings({ scan_interval_hours: parseInt(v, 10) })}
                            >
                                <SelectTrigger data-testid="engine-interval-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {INTERVALS.map((h) => (
                                        <SelectItem key={h} value={String(h)}>{h} hour{h > 1 ? 's' : ''}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm text-[color:var(--text-secondary)]">Min confidence (%)</Label>
                            <div className="flex gap-2">
                                <Input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={minConf}
                                    onChange={(e) => setMinConf(e.target.value)}
                                    data-testid="engine-min-confidence-input"
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="shrink-0"
                                    disabled={savingSettings || minConf === ''}
                                    onClick={() => saveSettings({ min_confidence: Math.max(0, Math.min(100, parseInt(minConf, 10) || 0)) })}
                                    data-testid="engine-save-confidence-btn"
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 mt-4 text-xs text-[color:var(--text-muted)]">
                        <Clock className="w-3.5 h-3.5" />
                        Last scan: {status?.last_scan_at ? new Date(status.last_scan_at).toLocaleString() : 'never'}
                        {status?.last_scan_summary && (
                            <span>· {status.last_scan_summary.drafts_created} draft(s) created</span>
                        )}
                    </div>
                </div>

                {/* Free channel teasers */}
                <div
                    className="rounded-lg p-5"
                    style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' }}
                    data-testid="free-teaser-card"
                >
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                        <h2 className="text-base font-semibold flex items-center gap-2">
                            <Megaphone className="w-4 h-4" style={{ color: '#229ED9' }} />
                            Free Channel Teasers (TP Hits)
                        </h2>
                        <div className="flex items-center gap-3">
                            <Label className="text-sm text-[color:var(--text-secondary)]">Enabled</Label>
                            <Switch
                                checked={settings.free_teaser_enabled !== false}
                                onCheckedChange={(v) => saveSettings({ free_teaser_enabled: v })}
                                disabled={savingSettings}
                                data-testid="free-teaser-toggle"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-[color:var(--text-muted)] mb-4">
                        When a signal hits TP, a <b>blurred chart</b> stamped "TP HIT" is posted to your free Telegram channel with an enrol CTA — social proof for prospects.
                        {tgStatus?.free_configured
                            ? <span className="text-emerald-600"> Connected ✓</span>
                            : <span className="text-amber-600"> Not connected — add @NikayofxBot as admin to the free channel, then Detect.</span>}
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="space-y-1.5 flex-1 min-w-[200px]">
                            <Label className="text-sm text-[color:var(--text-secondary)]">Free channel chat ID</Label>
                            <Input
                                value={freeChatId}
                                onChange={(e) => setFreeChatId(e.target.value)}
                                placeholder="-100XXXXXXXXXX"
                                data-testid="free-chat-id-input"
                            />
                        </div>
                        <Button variant="outline" size="sm" onClick={detectFreeChat} disabled={freeBusy} data-testid="free-detect-btn">
                            Detect
                        </Button>
                        <Button size="sm" onClick={saveFreeChat} disabled={freeBusy || !freeChatId.trim()} data-testid="free-save-btn">
                            Save
                        </Button>
                        <Button variant="outline" size="sm" onClick={testFreeChat} disabled={freeBusy || !tgStatus?.free_configured} data-testid="free-test-btn">
                            Send Test
                        </Button>
                    </div>
                </div>

                {/* Arabic channels */}
                <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' }} data-testid="arabic-channels-card">
                    <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-lg font-semibold">Arabic Channels 🇸🇦</h2>
                        {arStatus?.vip_configured && arStatus?.free_configured && <span className="text-emerald-600 text-xs font-bold">Connected ✓</span>}
                    </div>
                    <p className="text-xs text-[color:var(--text-muted)] mb-4">
                        Signals + outcomes mirror to the <b>Arabic VIP</b> group (in Arabic) and TP-hit teasers to the <b>Arabic Free</b> group.
                        Approved Arabic-country students get invited to the Arabic VIP group automatically.
                        To detect: send "vip arabic" in the VIP group and "free arabic" in the free group, then click Detect.
                    </p>
                    <div className="flex flex-wrap items-end gap-2">
                        <div className="space-y-1.5 flex-1 min-w-[170px]">
                            <Label className="text-sm text-[color:var(--text-secondary)]">VIP group chat ID</Label>
                            <Input value={arVip} onChange={(e) => setArVip(e.target.value)} placeholder="-100XXXXXXXXXX" data-testid="ar-vip-chat-input" />
                        </div>
                        <div className="space-y-1.5 flex-1 min-w-[170px]">
                            <Label className="text-sm text-[color:var(--text-secondary)]">Free group chat ID</Label>
                            <Input value={arFree} onChange={(e) => setArFree(e.target.value)} placeholder="-100XXXXXXXXXX" data-testid="ar-free-chat-input" />
                        </div>
                        <Button variant="outline" size="sm" onClick={detectArabic} disabled={arBusy} data-testid="arabic-detect-btn">Detect</Button>
                        <Button size="sm" onClick={saveArabic} disabled={arBusy} data-testid="arabic-save-btn">Save</Button>
                        <Button variant="outline" size="sm" onClick={testArabic} disabled={arBusy} data-testid="arabic-test-btn">Send Test</Button>
                    </div>
                </div>

                {/* Pending drafts */}
                <section className="space-y-4">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        Pending Drafts
                        {drafts.length > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: '#F59E0B' }} data-testid="pending-drafts-count">
                                {drafts.length}
                            </span>
                        )}
                    </h2>
                    {loading ? (
                        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--text-muted)]" /></div>
                    ) : drafts.length === 0 ? (
                        <div
                            className="rounded-lg p-8 text-center text-sm text-[color:var(--text-muted)]"
                            style={{ backgroundColor: 'var(--background-surface)', border: '1px dashed var(--border-subtle)' }}
                            data-testid="no-drafts-message"
                        >
                            <AlertTriangle className="w-5 h-5 mx-auto mb-2 opacity-50" />
                            No pending drafts. Run a scan to analyse the markets for CRT setups.
                        </div>
                    ) : (
                        drafts.map((d) => (
                            <DraftCard key={d.id} draft={d} onApprove={approve} onReject={reject} deciding={decidingId === d.id} />
                        ))
                    )}
                </section>

                {/* Recent decisions */}
                {history.length > 0 && (
                    <section className="space-y-3">
                        <h2 className="text-lg font-semibold">Recent Decisions</h2>
                        <div className="space-y-2">
                            {history.map((d) => (
                                <div
                                    key={d.id}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
                                    style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' }}
                                    data-testid={`decided-draft-${d.market}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="font-semibold">{d.market}</span>
                                        <DirectionBadge direction={d.direction} />
                                        <span className="text-[color:var(--text-muted)] text-xs">@{d.entry_price}</span>
                                    </div>
                                    <span
                                        className="text-xs font-bold px-2 py-0.5 rounded"
                                        style={d.status === 'approved'
                                            ? { backgroundColor: 'rgba(16,185,129,0.15)', color: '#10B981' }
                                            : { backgroundColor: 'rgba(239,68,68,0.12)', color: '#EF4444' }}
                                    >
                                        {d.status.toUpperCase()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
}
